import { Folder, Download, Trash2, Pencil } from "lucide-react";
import { fileIcon, fmtSize, fmtDate } from "./driveUtils";

export default function DriveListView({
  items, selected, renaming, renameValue, setRenameValue,
  onOpen, onToggleSelect, onDownload, onDelete, onStartRename, onSubmitRename
}) {
  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-border">
          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-8" />
          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Modified</th>
          <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Size</th>
          <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <ListRow
            key={item.path}
            item={item}
            selected={selected}
            renaming={renaming}
            renameValue={renameValue}
            setRenameValue={setRenameValue}
            onOpen={onOpen}
            onToggleSelect={onToggleSelect}
            onDownload={onDownload}
            onDelete={onDelete}
            onStartRename={onStartRename}
            onSubmitRename={onSubmitRename}
          />
        ))}
      </tbody>
    </table>
  );
}

function ListRow({ item, selected, renaming, renameValue, setRenameValue,
  onOpen, onToggleSelect, onDownload, onDelete, onStartRename, onSubmitRename }) {
  const isSelected = selected.has(item.path);
  const isRenaming = renaming === item.path;

  return (
    <tr
      onDoubleClick={() => onOpen(item)}
      className={`border-b border-border cursor-pointer transition-colors group ${
        isSelected ? "bg-[#F5F4EE]" : "hover:bg-[#F8F9FA]"
      }`}
      data-testid={`list-item-${item.name}`}
    >
      <td className="py-2 px-3">
        <div
          onClick={(e) => onToggleSelect(e, item.path)}
          className={`w-4 h-4 border flex items-center justify-center cursor-pointer ${
            isSelected ? "border-[#A8A67A] bg-[#A8A67A]" : "opacity-0 group-hover:opacity-100 border-border"
          }`}
        >
          {isSelected && <span className="text-white text-[10px] leading-none">✓</span>}
        </div>
      </td>
      <td className="py-2 px-3">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center">
            {item.type === "folder"
              ? <Folder className="w-5 h-5 text-amber-500" />
              : <div className="scale-75 origin-center">{fileIcon(item)}</div>}
          </div>
          {isRenaming ? (
            <input
              autoFocus
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onBlur={() => onSubmitRename(item)}
              onKeyDown={e => { if (e.key === "Enter") onSubmitRename(item); if (e.key === "Escape") onSubmitRename(item); }}
              className="text-sm border border-[#A8A67A] px-1 py-0.5 focus:outline-none w-48"
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <span className="font-medium text-foreground text-sm">{item.name}</span>
          )}
        </div>
      </td>
      <td className="py-2 px-3 text-xs text-muted-foreground">{fmtDate(item.modified)}</td>
      <td className="py-2 px-3 text-xs text-muted-foreground font-mono">
        {item.type === "folder" ? `${item.children ?? 0} items` : fmtSize(item.size)}
      </td>
      <td className="py-2 px-3 text-right">
        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {item.type === "file" && (
            <button onClick={(e) => { e.stopPropagation(); onDownload(item.path); }}
              className="p-1.5 text-muted-foreground hover:text-[#A8A67A] hover:bg-[#F5F4EE] transition-colors"
              title="Download" data-testid={`download-${item.name}`}>
              <Download className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); onStartRename(item); }}
            className="p-1.5 text-muted-foreground hover:text-[#A8A67A] hover:bg-[#F5F4EE] transition-colors"
            title="Rename" data-testid={`rename-${item.name}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(item.path); }}
            className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
            title="Delete" data-testid={`delete-${item.name}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
